/**
 * Automation Rule Builder Component
 * 
 * Visual interface for creating and editing automation rules for workflow stages.
 */

import React, { useState } from 'react';
import { Plus, Trash2, Save, X, Sparkles, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import type { StageAutomationRule, RuleCondition, RuleAction, RuleConditionGroup } from '@/types/automation-rules';
import { AutomationRuleService } from '@/services/automationRuleService';
import { RULE_FIELDS, RULE_ACTIONS, OPERATOR_LABELS, RULE_TEMPLATES } from '@/config/automationRuleFields';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface AutomationRuleBuilderProps {
  stageId: string;
  stageName: string;
  rules: StageAutomationRule[];
  onSave: (rules: StageAutomationRule[]) => void;
  onCancel: () => void;
}

export function AutomationRuleBuilder({ 
  stageId, 
  stageName, 
  rules, 
  onSave, 
  onCancel 
}: AutomationRuleBuilderProps) {
  const [editingRules, setEditingRules] = useState<StageAutomationRule[]>(rules);
  const [showTemplates, setShowTemplates] = useState(false);

  const addRule = () => {
    const newRule: StageAutomationRule = {
      id: `rule-${Date.now()}`,
      stageId,
      name: `Rule ${editingRules.length + 1}`,
      description: '',
      isEnabled: true,
      priority: 5,
      stopOnMatch: true,
      conditionGroup: {
        id: `group-${Date.now()}`,
        operator: 'AND',
        conditions: [],
        groups: []
      },
      actions: [],
      createdAt: new Date(),
      createdBy: 'current-user', // TODO: Get from auth context
      executionCount: 0,
      matchCount: 0
    };
    setEditingRules([...editingRules, newRule]);
  };

  const addRuleFromTemplate = (templateId: string) => {
    const template = RULE_TEMPLATES.find(t => t.id === templateId);
    if (!template) return;

    const newRule: StageAutomationRule = {
      ...template.template,
      id: `rule-${Date.now()}`,
      stageId,
      createdAt: new Date(),
      createdBy: 'current-user',
      executionCount: 0,
      matchCount: 0
    };

    setEditingRules([...editingRules, newRule]);
    setShowTemplates(false);
    toast.success('Rule added from template');
  };

  const handleSave = () => {
    // Validate all rules
    const errors: string[] = [];
    editingRules.forEach((rule, index) => {
      const validation = AutomationRuleService.validateRule(rule);
      if (!validation.isValid) {
        errors.push(`Rule ${index + 1} (${rule.name}): ${validation.errors.join(', ')}`);
      }
    });

    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return;
    }

    onSave(editingRules);
    toast.success('Automation rules saved successfully');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Automation Rules
          </h2>
          <p className="text-muted-foreground mt-1">
            Configure automation rules for stage: <span className="font-medium">{stageName}</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Save Rules
          </Button>
        </div>
      </div>

      {/* Info Alert */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Rules are evaluated in priority order (1 = highest). When a rule matches, its actions are executed.
          If "Stop on match" is enabled, no further rules will be evaluated.
        </AlertDescription>
      </Alert>

      {/* Empty State or Rules List */}
      {editingRules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No automation rules configured</h3>
            <p className="text-muted-foreground mb-6">
              Create your first automation rule to streamline approvals
            </p>
            <div className="flex gap-2 justify-center">
              <Button onClick={addRule}>
                <Plus className="h-4 w-4 mr-2" />
                Create Rule
              </Button>
              <Button variant="outline" onClick={() => setShowTemplates(!showTemplates)}>
                Use Template
              </Button>
            </div>

            {/* Template Selection */}
            {showTemplates && (
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                {RULE_TEMPLATES.map(template => (
                  <Card key={template.id} className="cursor-pointer hover:border-primary" onClick={() => addRuleFromTemplate(template.id)}>
                    <CardHeader>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <CardDescription>{template.description}</CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Rules List */}
          {editingRules.map((rule, index) => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onUpdate={(updated) => {
                const newRules = [...editingRules];
                newRules[index] = updated;
                setEditingRules(newRules);
              }}
              onDelete={() => {
                setEditingRules(editingRules.filter((_, i) => i !== index));
                toast.success('Rule deleted');
              }}
              onDuplicate={() => {
                const duplicated = {
                  ...rule,
                  id: `rule-${Date.now()}`,
                  name: `${rule.name} (Copy)`,
                  createdAt: new Date()
                };
                setEditingRules([...editingRules, duplicated]);
                toast.success('Rule duplicated');
              }}
            />
          ))}

          {/* Add Rule Buttons */}
          <div className="flex gap-2">
            <Button onClick={addRule} variant="outline" className="flex-1">
              <Plus className="h-4 w-4 mr-2" />
              Add Another Rule
            </Button>
            <Button variant="outline" onClick={() => setShowTemplates(!showTemplates)}>
              Use Template
            </Button>
          </div>

          {/* Template Selection */}
          {showTemplates && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {RULE_TEMPLATES.map(template => (
                <Card key={template.id} className="cursor-pointer hover:border-primary" onClick={() => addRuleFromTemplate(template.id)}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{template.name}</CardTitle>
                        <CardDescription className="mt-1">{template.description}</CardDescription>
                      </div>
                      <Badge variant="outline">{template.category}</Badge>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Rule Card Component
// ============================================================================

interface RuleCardProps {
  rule: StageAutomationRule;
  onUpdate: (rule: StageAutomationRule) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

function RuleCard({ rule, onUpdate, onDelete, onDuplicate }: RuleCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Card className={!rule.isEnabled ? 'opacity-60' : ''}>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Input
                value={rule.name}
                onChange={(e) => onUpdate({ ...rule, name: e.target.value })}
                className="text-lg font-semibold"
                placeholder="Rule name"
              />
              <Badge variant={rule.isEnabled ? 'default' : 'secondary'}>
                Priority {rule.priority}
              </Badge>
            </div>
            {rule.description && (
              <p className="text-sm text-muted-foreground">{rule.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={rule.isEnabled}
              onCheckedChange={(checked) => onUpdate({ ...rule, isEnabled: checked })}
            />
            <Button variant="ghost" size="sm" onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? 'Collapse' : 'Expand'}
            </Button>
            <Button variant="ghost" size="sm" onClick={onDuplicate}>
              Copy
            </Button>
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-6">
          {/* Description */}
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Textarea
              value={rule.description || ''}
              onChange={(e) => onUpdate({ ...rule, description: e.target.value })}
              placeholder="Describe what this rule does..."
              rows={2}
            />
          </div>

          <Separator />

          {/* Conditions */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">When a timesheet reaches this stage:</Label>
            
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <div className="font-medium text-sm">IF all of these conditions are met:</div>
              
              <ConditionGroupBuilder
                conditionGroup={rule.conditionGroup}
                onChange={(group) => onUpdate({ ...rule, conditionGroup: group })}
              />
            </div>
          </div>

          <Separator />

          {/* Actions */}
          <div className="space-y-3">
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <div className="font-medium text-sm">THEN automatically:</div>
              
              <ActionListBuilder
                actions={rule.actions}
                onChange={(actions) => onUpdate({ ...rule, actions })}
              />
            </div>
          </div>

          <Separator />

          {/* Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Priority (1 = highest)</Label>
              <Select
                value={String(rule.priority)}
                onValueChange={(value) => onUpdate({ ...rule, priority: Number(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(p => (
                    <SelectItem key={p} value={String(p)}>
                      {p} {p === 1 ? '(Highest)' : p === 10 ? '(Lowest)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Execution Behavior</Label>
              <div className="flex items-center gap-2 h-10">
                <Switch
                  checked={rule.stopOnMatch}
                  onCheckedChange={(checked) => onUpdate({ ...rule, stopOnMatch: checked })}
                />
                <span className="text-sm">Stop evaluating other rules if this matches</span>
              </div>
            </div>
          </div>

          {/* Statistics */}
          {rule.executionCount > 0 && (
            <>
              <Separator />
              <div className="flex gap-6 text-sm text-muted-foreground">
                <div>
                  <span className="font-medium">Executed:</span> {rule.executionCount} times
                </div>
                <div>
                  <span className="font-medium">Matched:</span> {rule.matchCount} times
                </div>
                {rule.lastMatchedAt && (
                  <div>
                    <span className="font-medium">Last matched:</span> {format(rule.lastMatchedAt, 'MMM d, yyyy')}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ============================================================================
// Condition Group Builder Component
// ============================================================================

interface ConditionGroupBuilderProps {
  conditionGroup: RuleConditionGroup;
  onChange: (group: RuleConditionGroup) => void;
}

function ConditionGroupBuilder({ conditionGroup, onChange }: ConditionGroupBuilderProps) {
  const addCondition = () => {
    const newCondition: RuleCondition = {
      id: `cond-${Date.now()}`,
      field: 'employee.department',
      operator: 'equals',
      value: ''
    };
    onChange({
      ...conditionGroup,
      conditions: [...conditionGroup.conditions, newCondition]
    });
  };

  const updateCondition = (index: number, updated: RuleCondition) => {
    const newConditions = [...conditionGroup.conditions];
    newConditions[index] = updated;
    onChange({ ...conditionGroup, conditions: newConditions });
  };

  const removeCondition = (index: number) => {
    onChange({
      ...conditionGroup,
      conditions: conditionGroup.conditions.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="space-y-2">
      {conditionGroup.conditions.map((condition, index) => (
        <ConditionBuilder
          key={condition.id}
          condition={condition}
          onChange={(updated) => updateCondition(index, updated)}
          onRemove={() => removeCondition(index)}
        />
      ))}

      {conditionGroup.conditions.length === 0 && (
        <div className="text-sm text-muted-foreground italic py-2">
          No conditions added yet. Click "Add Condition" to get started.
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={addCondition}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Condition
      </Button>
    </div>
  );
}

// ============================================================================
// Individual Condition Builder
// ============================================================================

interface ConditionBuilderProps {
  condition: RuleCondition;
  onChange: (condition: RuleCondition) => void;
  onRemove: () => void;
}

function ConditionBuilder({ condition, onChange, onRemove }: ConditionBuilderProps) {
  const fieldMetadata = RULE_FIELDS.find(f => f.field === condition.field);
  const supportedOperators = fieldMetadata?.supportedOperators || [];

  return (
    <div className="flex items-center gap-2 p-2 border rounded-lg bg-background">
      {/* Field Selection */}
      <Select
        value={condition.field}
        onValueChange={(value) => onChange({ ...condition, field: value as any })}
      >
        <SelectTrigger className="w-[200px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {RULE_FIELDS.map(field => (
            <SelectItem key={field.field} value={field.field}>
              {field.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Operator Selection */}
      <Select
        value={condition.operator}
        onValueChange={(value) => onChange({ ...condition, operator: value as any })}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {supportedOperators.map(op => (
            <SelectItem key={op} value={op}>
              {OPERATOR_LABELS[op]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value Input (only if not boolean operator) */}
      {!['isTrue', 'isFalse'].includes(condition.operator) && (
        <Input
          value={condition.value}
          onChange={(e) => onChange({ ...condition, value: e.target.value })}
          placeholder="value"
          className="flex-1"
        />
      )}

      {/* Remove Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ============================================================================
// Action List Builder Component
// ============================================================================

interface ActionListBuilderProps {
  actions: RuleAction[];
  onChange: (actions: RuleAction[]) => void;
}

function ActionListBuilder({ actions, onChange }: ActionListBuilderProps) {
  const addAction = () => {
    const newAction: RuleAction = {
      id: `action-${Date.now()}`,
      type: 'auto-approve',
      parameters: {}
    };
    onChange([...actions, newAction]);
  };

  const updateAction = (index: number, updated: RuleAction) => {
    const newActions = [...actions];
    newActions[index] = updated;
    onChange(newActions);
  };

  const removeAction = (index: number) => {
    onChange(actions.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      {actions.map((action, index) => (
        <ActionBuilder
          key={action.id}
          action={action}
          onChange={(updated) => updateAction(index, updated)}
          onRemove={() => removeAction(index)}
        />
      ))}

      {actions.length === 0 && (
        <div className="text-sm text-muted-foreground italic py-2">
          No actions added yet. Click "Add Action" to get started.
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={addAction}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Action
      </Button>
    </div>
  );
}

// ============================================================================
// Individual Action Builder
// ============================================================================

interface ActionBuilderProps {
  action: RuleAction;
  onChange: (action: RuleAction) => void;
  onRemove: () => void;
}

function ActionBuilder({ action, onChange, onRemove }: ActionBuilderProps) {
  const actionMetadata = RULE_ACTIONS.find(a => a.type === action.type);

  return (
    <div className="space-y-2 p-2 border rounded-lg bg-background">
      <div className="flex items-center gap-2">
        {/* Action Type Selection */}
        <Select
          value={action.type}
          onValueChange={(value) => onChange({ ...action, type: value as any, parameters: {} })}
        >
          <SelectTrigger className="flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RULE_ACTIONS.map(actionDef => (
              <SelectItem key={actionDef.type} value={actionDef.type}>
                {actionDef.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Remove Button */}
        <Button variant="ghost" size="sm" onClick={onRemove}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Action Parameters */}
      {action.type === 'add-note' && (
        <Textarea
          value={action.parameters.note || ''}
          onChange={(e) => onChange({
            ...action,
            parameters: { ...action.parameters, note: e.target.value }
          })}
          placeholder="Note text..."
          rows={2}
        />
      )}

      {action.type === 'flag' && (
        <div className="grid grid-cols-2 gap-2">
          <Input
            value={action.parameters.flagLabel || ''}
            onChange={(e) => onChange({
              ...action,
              parameters: { ...action.parameters, flagLabel: e.target.value }
            })}
            placeholder="Flag label"
          />
          <Select
            value={action.parameters.flagColor || 'blue'}
            onValueChange={(value) => onChange({
              ...action,
              parameters: { ...action.parameters, flagColor: value }
            })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="blue">Blue</SelectItem>
              <SelectItem value="green">Green</SelectItem>
              <SelectItem value="yellow">Yellow</SelectItem>
              <SelectItem value="orange">Orange</SelectItem>
              <SelectItem value="red">Red</SelectItem>
              <SelectItem value="purple">Purple</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {actionMetadata && !actionMetadata.requiresParameters && (
        <p className="text-xs text-muted-foreground">
          {actionMetadata.description}
        </p>
      )}
    </div>
  );
}
